'use client';

import { useTranslation } from '@/features/i18n';
import { Button } from '@/ui/buttons/Button';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { useAuthentication } from '@vimond/features/authentication';
import {
  CouponValidationResult,
  validateCoupon,
} from '@vimond/services/coupon';
import React, { useCallback, useState } from 'react';

type ApplyCouponProps = {
  onCouponApplied?: (
    results: CouponValidationResult[],
    couponCode: string,
  ) => void;
  productPaymentIds?: number[];
};

export const ApplyCoupon: React.FC<ApplyCouponProps> = ({
  onCouponApplied,
  productPaymentIds,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { jwt } = useAuthentication();

  const { t } = useTranslation();

  const INVALID_CODE_MESSAGE = t('voucher.invalid-code');

  const handleApply = useCallback(async () => {
    const sanitizedInput = coupon.trim().toUpperCase();

    if (!sanitizedInput || !jwt) {
      setError(INVALID_CODE_MESSAGE);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const validationResults = await validateCoupon({
        couponCode: sanitizedInput,
        productPaymentIds: productPaymentIds ?? [],
        jwt,
      });

      const couponValidForAllProducts =
        validationResults.length > 0 && validationResults.every((r) => r.valid);

      if (couponValidForAllProducts) {
        setAppliedCoupon(sanitizedInput);
        setExpanded(false);
        onCouponApplied?.(validationResults, sanitizedInput);
      } else {
        setError(INVALID_CODE_MESSAGE);
      }
    } catch {
      setError(INVALID_CODE_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, [coupon, jwt, productPaymentIds, onCouponApplied, INVALID_CODE_MESSAGE]);

  const handleRemove = useCallback(() => {
    setAppliedCoupon(null);
    setCoupon('');
    setError(null);
    onCouponApplied?.([], '');
  }, [onCouponApplied]);

  if (!jwt || !productPaymentIds?.length) {
    return null;
  }

  return (
    <div className="flex justify-center items-center w-full px-1 sm:px-6 md:px-8">
      <div className="w-full max-w-sm sm:max-w-md md:max-w-sm lg:max-w-md xl:max-w-lg rounded-lg bg-bg-primary px-1 sm:px-6">
        {/* Show label only if no coupon is applied */}
        {!appliedCoupon && (
          <button
            className="flex items-center w-full text-base md:text-lg font-medium text-text-primary hover:text-accent transition-colors mb-2 px-1 sm:px-2"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            type="button"
          >
            <span className="flex items-center gap-1">
              <TagIcon className="h-5 w-5" />
              {t('voucher.input-label')}
              {expanded ? (
                <ChevronUpIcon className="h-5 w-5 ml-1" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 ml-1" />
              )}
            </span>
          </button>
        )}

        {/* Applied coupon state */}
        {appliedCoupon && !expanded && (
          <div className="flex items-center justify-between gap-3 text-sm text-text-primary w-full rounded-lg border border-green-300 px-3 py-1">
            <div className="flex items-center gap-1">
              <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
              <span className="hidden md:inline">
                {t('voucher.discount-code')}
              </span>
              <span className="font-semibold tracking-wide truncate">
                {appliedCoupon}
              </span>
              <span className="whitespace-nowrap">{t('voucher.applied')}!</span>
            </div>
            <Button
              variant="text"
              onClick={handleRemove}
              className="text-blue-500"
              aria-label={t('voucher.remove-coupon')}
              type="button"
            >
              {t('voucher.change')}
            </Button>
          </div>
        )}

        {/* Expanded input state */}
        {expanded && !appliedCoupon && (
          <div className="flex flex-row w-full gap-2 mt-2">
            <input
              type="text"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading && coupon.trim()) {
                  void handleApply();
                }
                if (e.key === 'Escape') {
                  setExpanded(false);
                  setError(null);
                  setCoupon('');
                }
              }}
              className={`flex-1 min-w-0 rounded-lg border-2 ${error ? 'border-red-500 focus:border-red-500' : 'border-border-primary focus:border-accent'} bg-bg-secondary px-3 sm:px-4 py-2 sm:py-2.5 text-text-primary font-mono uppercase tracking-wide placeholder:font-sans placeholder:normal-case placeholder:tracking-normal focus:outline-none dark:bg-bg-secondary-dark dark:text-text-primary-dark text-sm sm:text-base transition-all duration-200`}
              disabled={loading}
              autoFocus
              maxLength={20}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="characters"
              aria-label="Discount code"
              aria-invalid={!!error}
              aria-describedby={error ? 'coupon-error' : undefined}
            />
            <Button
              variant="primary"
              className="w-24 sm:w-28 text-sm sm:text-base"
              onClick={handleApply}
              disabled={loading || !coupon.trim()}
              type="button"
            >
              {loading ? t('voucher.checking') : t('voucher.apply')}
            </Button>
          </div>
        )}
        {/* Error message always below input/button */}
        {error && expanded && !appliedCoupon && (
          <div className="text-red-600 dark:text-red-400 pt-2 text-xs sm:text-sm font-medium">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplyCoupon;
